// useDropbox.js
import { useEffect, useState } from "react";
import { Dropbox } from "dropbox";

const useDropbox = (dropboxAppKey, dropboxRedirectUri) => {
  // Initialize the access token from localStorage if it's still valid.
  const [accessToken, setAccessToken] = useState(() => {
    const storedToken = localStorage.getItem("dropboxAccessToken");
    const tokenExpiry = localStorage.getItem("dropboxTokenExpiry");
    if (storedToken && new Date().getTime() < tokenExpiry) {
      return storedToken;
    }
    return null;
  });

  // Optionally, initialize a Dropbox client instance (if needed later)
  const [dbx, setDbx] = useState(null);

  // Load the Dropbox Chooser script.
  useEffect(() => {
    if (!document.getElementById("dropboxjs")) {
      const dropboxScript = document.createElement("script");
      dropboxScript.src = "https://www.dropbox.com/static/api/2/dropins.js";
      dropboxScript.id = "dropboxjs";
      dropboxScript.setAttribute("data-app-key", dropboxAppKey);
      document.body.appendChild(dropboxScript);

      return () => {
        if (dropboxScript.parentNode) {
          dropboxScript.parentNode.removeChild(dropboxScript);
        }
      };
    }
  }, [dropboxAppKey]);

  // Initialize a Dropbox client if an access token is available.
  useEffect(() => {
    if (accessToken) {
      const dropbox = new Dropbox({ accessToken });
      setDbx(dropbox);
    }
  }, [accessToken]);

  /**
   * Initiates the Dropbox OAuth2 authentication flow.
   * Opens a pop-up window where the user can grant access to Dropbox.
   * Once authenticated, the access token and its expiry are stored locally.
   */
  const authenticateDropbox = () => {
    const authUrl = `https://www.dropbox.com/oauth2/authorize?client_id=${dropboxAppKey}&response_type=token&redirect_uri=${encodeURIComponent(
      dropboxRedirectUri
    )}&token_access_type=online&scope=files.content.read%20files.content.write%20sharing.write`;

    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const authWindow = window.open(
      authUrl,
      "DropboxAuth",
      `width=${width},height=${height},top=${top},left=${left}`
    );

    // Poll the authentication window for the access token.
    const interval = setInterval(() => {
      try {
        if (authWindow.closed) {
          clearInterval(interval);
        }
        const hash = authWindow.location.hash;
        if (hash) {
          const params = new URLSearchParams(hash.substring(1));
          const token = params.get("access_token");
          const expiresIn = params.get("expires_in");

          if (token) {
            const expiryTime = new Date().getTime() + expiresIn * 1000;
            localStorage.setItem("dropboxAccessToken", token);
            localStorage.setItem("dropboxTokenExpiry", expiryTime);
            setAccessToken(token);
            authWindow.close();
            clearInterval(interval);
          }
        }
      } catch (error) {
        // Cross-origin errors are expected until the authWindow redirects to our redirect URI.
      }
    }, 500);
  };

  /**
   * Opens the Dropbox Chooser so the user can select files.
   *
   * @param {Function} onSuccess - Callback invoked with the selected files.
   */
  const openDropboxChooser = (onSuccess) => {
    if (!window.Dropbox) {
      console.error("Dropbox Chooser script not loaded yet.");
      return;
    }

    const options = {
      success: (files) => {
        onSuccess(files);
      },
      cancel: () => {
        console.log("Dropbox Chooser canceled");
      },
      linkType: "preview", // Returns a preview link for the file.
      multiselect: true,   // Allow multiple file selection.
    };

    window.Dropbox.choose(options);
  };

  return {
    authenticateDropbox,
    openDropboxChooser,
    isDropboxAuthenticated: !!accessToken,
  };
};

export default useDropbox;
